import React from "react";
import { Box, Paper, Typography, Button, Chip, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
         Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Alert, LinearProgress, List, ListItem, ListItemText } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import { enqueueSnackbar } from "notistack";
import api from "../../Auth/Api";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { subscribePoll } from "../../Layout/Poller";
import { PageContainer, SectionHeader, SelectorBar, SelectField, StatCard, relTime } from "../../Layout/Common";

// LiveOps → SDLB: one event of a season daily leaderboard as the backend runs it (group `sdlb`): its days with the
// current step highlighted, countdowns to the next step and the next event, per-day rank statistics (Inspect), and
// the reimport from sheets with its preview of what changes on the running event (edit role).
// Deep link: /<game>/liveops/sdlb/<sdlbId>/<branch>[/<eventVersion>[/<step>]].
const REFRESH_MS = 60000;
const IMPORT_POLL_MS = 3000;
const BRANCHES = ["Production", "Beta", "Internal", "Development"];
const FIXED = { "& table": { tableLayout: "fixed" }, "& .MuiTableCell-root": { px: 1, fontSize: 13 } };
const sdlb = (description, parts, options) => api.group("sdlb")(description, parts, options);
const num = (v) => (v == null ? "—" : Math.round(v).toLocaleString());
const LATEST = "latest";   // the Event selector's default (MUI shows "" as no selection)
// SDLB days are UTC-aligned (a step starts on a UTC hour), so every date on the page is UTC: "09-14 00:00"
const utc = (iso) => (iso ? new Date(iso).toISOString().slice(5, 16).replace("T", " ") : "—");
const utcFull = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16).replace("T", " ") + " UTC" : "—");
const canEdit = () => {
    if (globals.loginData?.titles?.isMaster) return true;
    const roles = globals.loginData?.titles?.roles?.[globals.selectedTitle] ?? [];
    return roles.includes("liveops");
};

// Scores travel as the game stores them: Time modes in milliseconds, Points modes as plain numbers
const score = (v, mode) => {
    if (v == null) return "—";
    if (!mode || !mode.startsWith("Time")) return num(v);
    const ms = Math.round(v), s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}.${String(ms % 1000).padStart(3, "0")}`;
};
// A rank's reward metadata is the JSON the client shows ({hc, sc, gr, skins}); anything else is shown as is
const reward = (label) => {
    if (!label) return "";
    try {
        const j = JSON.parse(label);
        const parts = ["hc", "sc", "gr"].filter(k => j[k]).map(k => `${j[k]} ${k}`);
        if (Array.isArray(j.skins) && j.skins.length) parts.push(j.skins.join(", "));
        return parts.join(" · ") || label;
    } catch { return label; }
};
// Countdown to an ISO date against the server clock ("1h 05m", "12s"), "now" once it passed
const countdown = (iso, serverNow) => {
    if (!iso) return "";
    const s = Math.floor((new Date(iso).getTime() - serverNow) / 1000);
    if (s <= 0) return "now";
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
    return d ? `${d}d ${h}h ${String(m).padStart(2, "0")}m` : h ? `${h}h ${String(m).padStart(2, "0")}m` : m ? `${m}m ${String(r).padStart(2, "0")}s` : `${r}s`;
};
const nextEventText = (n) => (n ? `${n.eventId} at ${utcFull(n.startDate)}${n.sameRow ? " (the same row again)" : ""}` : "none scheduled");

export default class SdlbPage extends React.Component {
    constructor(props) {
        super(props);
        const deep = globals.route?.deep ?? [];
        this.state = {
            ids: null, sdlbId: deep[0] ?? "", branch: BRANCHES.includes(deep[1]) ? deep[1] : "Development",
            eventVersion: deep[2] ? Number(deep[2]) : null, openStep: deep[3] != null ? Number(deep[3]) : null,
            events: [], data: null, loadedAt: null, offset: 0, now: Date.now(), loading: false,
            inspect: null,    // { step, data, loading }
            reimport: null,   // { phase: importing | preview | applying | done, job, preview, result }
        };
    }

    componentDidMount() {
        this.clock = setInterval(() => this.setState({ now: Date.now() }), 1000);
        this.unsubscribe = subscribePoll(() => this.load(true), REFRESH_MS);
        sdlb("Loading SDLB ids", ["list"], { silent: true })
            .then(r => {
                const ids = r?.sdlbIds ?? [];
                this.setState({ ids, sdlbId: ids.includes(this.state.sdlbId) ? this.state.sdlbId : (ids[0] ?? "") }, () => this.load());
            })
            .catch(() => this.setState({ ids: [] }));
    }

    componentWillUnmount() {
        clearInterval(this.clock);
        clearTimeout(this.importTimer);
        this.unsubscribe?.();
    }

    serverNow = () => this.state.now + this.state.offset;

    // The event list (selector) and the selected event; the poll refreshes both and the open Inspect dialog
    load = (poll = false) => {
        const { sdlbId, branch, eventVersion, openStep } = this.state;
        if (!sdlbId) return Promise.resolve();
        if (!poll) this.setState({ loading: true });
        const parts = ["event", sdlbId, branch];
        if (eventVersion != null) parts.push(eventVersion);
        return Promise.all([
            sdlb("Loading SDLB events", ["events", sdlbId, branch], { silent: true }),
            sdlb("Loading SDLB event", parts, { silent: true }),
        ]).then(([ev, data]) => {
            if (!data || !data.serverTime) return;
            const offset = new Date(data.serverTime).getTime() - Date.now();
            // the Inspect calls read this.state.data, so they wait for the state to land
            this.setState({ events: ev?.events ?? [], data, offset, loadedAt: Date.now(), loading: false }, () => {
                const { inspect } = this.state;
                replaceDeep([sdlbId, branch, ...(data.event ? [data.event.eventVersion] : []), ...(inspect ? [inspect.step] : [])]);
                if (!poll && openStep != null && data.event) this.inspect(openStep);
                else if (poll && inspect && !inspect.data?.finished) this.inspect(inspect.step, true);
            });
        }).catch(() => this.setState({ loading: false }));
    };

    select = (patch) => this.setState({ ...patch, eventVersion: patch.eventVersion === undefined ? null : patch.eventVersion, inspect: null, openStep: null, data: null }, () => this.load());

    inspect = (step, quiet = false) => {
        const { sdlbId, branch, data } = this.state;
        if (!data?.event) return;
        if (!quiet) this.setState({ inspect: { step, data: this.state.inspect?.step === step ? this.state.inspect.data : null, loading: true } });
        sdlb("Loading step", ["step", sdlbId, branch, data.event.eventVersion, step], { silent: true })
            .then(r => {
                if (!r || r.ranks == null) return;
                this.setState({ inspect: { step, data: r, loading: false } });
                replaceDeep([sdlbId, branch, data.event.eventVersion, step]);
            })
            .catch(() => this.setState(s => ({ inspect: s.inspect && { ...s.inspect, loading: false } })));
    };

    closeInspect = () => {
        const { sdlbId, branch, data } = this.state;
        this.setState({ inspect: null, openStep: null });
        replaceDeep([sdlbId, branch, ...(data?.event ? [data.event.eventVersion] : [])]);
    };

    // ---------- reimport ----------
    // Import the meta sheets (a title-wide job), then preview the result against this branch; the apply creates the
    // patch and edits the running events. A refused apply (a step advanced, the hourly job is near) asks for a new preview.
    reimportStart = () => {
        this.setState({ reimport: { phase: "importing", job: null } });
        sdlb("Importing from sheets", ["reimport-start"], { silent: true })
            .then(job => this.reimportTrack(job))
            .catch(e => this.setState({ reimport: { phase: "failed", errors: [e.message] } }));
    };

    reimportTrack = (job) => {
        if (!job || typeof job !== "object") { this.setState({ reimport: { phase: "failed", errors: [String(job)] } }); return; }
        if (job.status === "running") {
            this.setState({ reimport: { phase: "importing", job } });
            this.importTimer = setTimeout(() => sdlb("Import status", ["reimport-status"], { silent: true }).then(this.reimportTrack).catch(() => this.reimportTrack(job)), IMPORT_POLL_MS);
            return;
        }
        if (job.status !== "done") { this.setState({ reimport: { phase: "failed", job, errors: job.errors ?? ["The import failed."] } }); return; }
        this.reimportPreview(job);
    };

    reimportPreview = (job) => {
        this.setState({ reimport: { phase: "previewing", job } });
        sdlb("Loading the preview", ["reimport-preview", this.state.branch], { silent: true })
            .then(preview => {
                if (!preview || typeof preview !== "object") { this.setState({ reimport: { phase: "failed", errors: [String(preview)] } }); return; }
                this.setState({ reimport: { phase: "preview", job, preview } });
            })
            .catch(e => this.setState({ reimport: { phase: "failed", errors: [e.message] } }));
    };

    reimportApply = () => {
        const { reimport, branch } = this.state;
        const p = reimport?.preview;
        if (!p) return;
        this.setState({ reimport: { ...reimport, phase: "applying" } });
        sdlb("Applying the reimport", ["reimport-apply", branch, p.tempId], { payload: { fingerprint: p.fingerprint }, successMessage: "Reimport applied" })
            .then(result => {
                this.setState({ reimport: { phase: "done", preview: p, result } });
                (result?.failures ?? []).forEach(f => enqueueSnackbar(f, { variant: "warning", persist: true }));
                this.load();
            })
            .catch(() => this.setState({ reimport: { ...reimport, phase: "preview", refused: true } }));
    };

    closeReimport = () => { clearTimeout(this.importTimer); this.setState({ reimport: null }); };

    // ---------- render ----------
    renderInfo() {
        const { data } = this.state;
        const serverNow = this.serverNow();
        const e = data.event, next = data.next ?? {};
        const nextEvent = next.event;
        const currentDay = e && !e.isDone ? data.days[e.currentStep] : null;
        return (
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <StatCard label="Event" value={e ? `${e.eventId} · v${e.eventVersion}` : "none"}
                          secondary={e ? `${utcFull(e.startDate)} → ${utc(e.endDate)}${e.stepInterval > 0 ? ` · ${e.stepDuration} h steps + ${e.stepInterval} h pause` : ""}` : "no event has run on this branch"}
                          tertiary={e ? (e.isDone ? "finished" : `ends in ${countdown(e.endDate, serverNow)}`) : null} />
                <StatCard label="Current step" value={e && !e.isDone ? `${e.currentStep + 1} / ${e.totalSteps}` : "—"}
                          secondary={e && !e.isDone ? `${e.currentLevel} · ${e.scoreMode}` : ""}
                          tertiary={e?.paused ? `paused · opens in ${countdown(e.openDate, serverNow)}` : currentDay ? `${num(currentDay.players)} players so far` : null} />
                <StatCard label="Next step" value={e && !e.isDone ? countdown(next.stepDate, serverNow) : "—"}
                          secondary={e && !e.isDone ? (next.eventEnds ? "the event ends" : `level ${next.stepLevel ?? "?"}`) : ""}
                          tertiary={e && !e.isDone ? utcFull(next.stepDate) : null} />
                <StatCard label="Next event" value={nextEvent ? countdown(nextEvent.startDate, serverNow) : "none scheduled"}
                          secondary={nextEvent ? `${nextEvent.eventId}${nextEvent.sameRow ? " (the same schedule row again)" : ""}` : `meta patch ${data.metaPatch}`}
                          tertiary={nextEvent ? `${utcFull(nextEvent.startDate)} · row ${utc(nextEvent.scheduleStart)} → ${utc(nextEvent.scheduleEnd)}` : null} />
            </Box>
        );
    }

    renderActions() {
        const { data, reimport, branch } = this.state;
        const e = data?.event;
        const edits = e?.edits ?? [];
        return (
            <Paper sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="subtitle1" sx={{ mr: 1 }}>Actions</Typography>
                <Tooltip title={`Import the meta sheets again, then review what changes for ${branch} - including the running event - before it is applied`}>
                    <span><Button variant="contained" size="small" disabled={!!reimport} onClick={this.reimportStart}>Reimport from sheets</Button></span>
                </Tooltip>
                {edits.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                        last edit {relTime(edits[edits.length - 1].date)} by {edits[edits.length - 1].by} (patch {edits[edits.length - 1].patch}): {edits[edits.length - 1].changes.join("; ")}
                    </Typography>
                )}
            </Paper>
        );
    }

    renderDays() {
        const { data } = this.state;
        const cols = [
            { id: "step", label: "Step", width: "10%", align: "right" },
            { id: "date", label: "Date (UTC)", width: "32%" },
            { id: "level", label: "Level", width: "26%" },
            { id: "players", label: "Players", width: "14%", align: "right" },
            { id: "inspect", label: "", width: "18%", align: "right" },
        ];
        return (
            <TableContainer component={Paper} sx={FIXED}>
                <Table size="small">
                    <TableHead>
                        <TableRow>{cols.map(c => <TableCell key={c.id} align={c.align} sx={{ width: c.width }}>{c.label}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                        {data.days.map(d => (
                            <TableRow key={d.step} selected={d.state === "current"}
                                      sx={d.state === "current" ? { "& .MuiTableCell-root": { fontWeight: 600 } } : d.state === "future" ? { "& .MuiTableCell-root": { color: "text.disabled" } } : {}}>
                                <TableCell align="right">{d.step + 1}{d.state === "current" && <Chip size="small" color={data.event?.paused ? "default" : "primary"} label={data.event?.paused ? "paused" : "now"} sx={{ ml: 1, height: 18 }} />}</TableCell>
                                <TableCell>{utc(d.start)} → {utc(d.end)}{data.event?.stepInterval > 0 && d.step < data.event.totalSteps - 1 && <Typography component="span" variant="caption" color="text.secondary"> · {data.event.stepInterval} h pause</Typography>}</TableCell>
                                <TableCell>{d.levelId ?? "—"}</TableCell>
                                <TableCell align="right">{d.state === "future" ? "—" : num(d.players)}</TableCell>
                                <TableCell align="right">
                                    <Button size="small" disabled={d.state === "future"} onClick={() => this.inspect(d.step)}>Inspect</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    renderInspect() {
        const { inspect, data } = this.state;
        if (!inspect || !data?.event) return null;
        const e = data.event, s = inspect.data;
        const lastOpen = e.isDone ? e.totalSteps - 1 : e.currentStep;
        const cols = [
            { id: "rank", label: "Rank", width: "13%" },
            { id: "reward", label: "Reward", width: "17%" },
            { id: "total", label: "Players", width: "9%", align: "right" },
            { id: "scored", label: "Scored", width: "9%", align: "right" },
            { id: "avg", label: "Avg score", width: "10%", align: "right" },
            { id: "cutoff", label: "Promotion cutoff", width: "15%", align: "right" },
            { id: "demoted", label: "Demoted", width: "12%", align: "right" },
            { id: "demoteCutoff", label: "Demotion cutoff", width: "15%", align: "right" },
        ];
        const spread = (c, mode) => (c ? `${score(c.median, mode)} (${score(c.min, mode)} – ${score(c.max, mode)})` : "—");
        return (
            <Dialog open onClose={this.closeInspect} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton size="small" disabled={inspect.step <= 0} onClick={() => this.inspect(inspect.step - 1)}><ChevronLeftIcon /></IconButton>
                    <Chip label={e.eventId} color="primary" variant="outlined" />
                    <Typography variant="h6" sx={{ flex: 1, textAlign: "center" }}>{s?.levelId ?? data.days[inspect.step]?.levelId ?? "—"}</Typography>
                    <Typography variant="subtitle1">Step {inspect.step + 1} / {e.totalSteps}</Typography>
                    <IconButton size="small" disabled={inspect.step >= lastOpen} onClick={() => this.inspect(inspect.step + 1)}><ChevronRightIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {s && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {utcFull(s.start)} → {utc(s.end)} · cutoffs: median (min – max) across buckets{s.finished ? "" : " · running: cutoffs and drops are what the buckets would close with now"}
                        </Typography>
                    )}
                    {!s && inspect.loading && <Typography variant="body2">Loading…</Typography>}
                    {s && (
                        <TableContainer component={Paper} sx={FIXED}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>{cols.map(c => <TableCell key={c.id} align={c.align} sx={{ width: c.width }}>{c.label}</TableCell>)}</TableRow>
                                </TableHead>
                                <TableBody>
                                    {s.ranks.map(r => (
                                        <TableRow key={r.rank} sx={r.total === 0 ? { "& .MuiTableCell-root": { color: "text.disabled" } } : {}}>
                                            <TableCell>{r.rank + 1}<Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{Math.round(r.promoteRatio * 100)}% ↑{r.rank > 0 && r.demoteRatio > 0 ? ` ${Math.round(r.demoteRatio * 100)}% ↓` : ""}</Typography></TableCell>
                                            <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><Tooltip title={r.label ?? ""}><span>{reward(r.label)}</span></Tooltip></TableCell>
                                            <TableCell align="right"><Tooltip title={r.buckets > 1 ? `${r.buckets} buckets` : ""}><span>{num(r.total)}</span></Tooltip></TableCell>
                                            <TableCell align="right">{num(r.scored)}</TableCell>
                                            <TableCell align="right">{score(r.avgScore, r.mode)}</TableCell>
                                            <TableCell align="right">{spread(r.cutoff, r.mode)}</TableCell>
                                            <TableCell align="right"><Tooltip title={r.demoted > 0 ? `${num(r.demotedNoScore)} of them with no score` : ""}><span>{r.rank > 0 && r.demoteRatio > 0 ? num(r.demoted ?? 0) : "—"}</span></Tooltip></TableCell>
                                            <TableCell align="right">{spread(r.demoteCutoff, r.mode)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={this.closeInspect}>Close</Button></DialogActions>
            </Dialog>
        );
    }

    renderReimport() {
        const { reimport, branch } = this.state;
        if (!reimport) return null;
        const p = reimport.preview, r = reimport.result;
        const busy = ["importing", "previewing", "applying"].includes(reimport.phase);
        const counts = reimport.job?.counts;
        const title = reimport.phase === "done" ? `Patch ${r?.patch} applied on ${branch}`
            : p ? `Reimport preview · ${branch} patch ${p.currentPatch} → ${p.nextPatch}` : "Reimport from sheets";
        return (
            <Dialog open onClose={busy ? undefined : this.closeReimport} maxWidth="md" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    {reimport.phase === "importing" && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>Importing the meta sheets{counts ? ` · ${counts.done} / ${counts.tabs} tabs` : ""}…</Typography>
                            <LinearProgress variant={counts?.tabs ? "determinate" : "indeterminate"} value={counts?.tabs ? 100 * counts.done / counts.tabs : 0} />
                        </Box>
                    )}
                    {reimport.phase === "previewing" && <Typography variant="body2">Comparing the import with {branch}…</Typography>}
                    {reimport.phase === "applying" && <Box><Typography variant="body2" sx={{ mb: 1 }}>Applying…</Typography><LinearProgress /></Box>}
                    {reimport.phase === "failed" && (
                        <Alert severity="error">{(reimport.errors ?? []).map((e, i) => <div key={i}>{e}</div>)}</Alert>
                    )}
                    {reimport.phase === "done" && r && (
                        <Box>
                            {r.applied.map(a => (
                                <Alert key={a.sdlbId} severity="success" sx={{ mb: 1 }}>
                                    <b>{a.sdlbId}</b> event v{a.eventVersion}: {a.changes.length ? a.changes.join("; ") : "no change on the running event"}
                                </Alert>
                            ))}
                            {(r.failures ?? []).map((f, i) => <Alert key={i} severity="warning" sx={{ mb: 1 }}>{f}</Alert>)}
                        </Box>
                    )}
                    {(reimport.phase === "preview" || reimport.phase === "applying") && p && this.renderPreview(p, reimport.refused)}
                </DialogContent>
                <DialogActions>
                    {reimport.phase === "preview" && reimport.refused && <Button onClick={() => this.reimportPreview(reimport.job)}>Preview again</Button>}
                    <Button onClick={this.closeReimport} disabled={busy}>{reimport.phase === "preview" ? "Cancel" : "Close"}</Button>
                    {reimport.phase === "preview" && (
                        <Tooltip title={p?.hold ?? (!p?.canApply ? "Fix the blockers in the sheet first" : "")}>
                            <span><Button variant="contained" onClick={this.reimportApply} disabled={!p?.canApply || !!p?.hold || reimport.refused}>Apply</Button></span>
                        </Tooltip>
                    )}
                </DialogActions>
            </Dialog>
        );
    }

    renderPreview(p, refused) {
        const importedAt = p.importedAt ? `imported ${relTime(p.importedAt)} by ${p.importedBy}` : "";
        const line = (primary, secondary) => <ListItem dense disableGutters><ListItemText primary={primary} secondary={secondary} /></ListItem>;
        return (
            <Box>
                {refused && <Alert severity="warning" sx={{ mb: 1 }}>The apply was refused: the events moved on or the hourly job is near. Preview again.</Alert>}
                {p.hold && <Alert severity="info" sx={{ mb: 1 }}>{p.hold}</Alert>}
                {(p.warnings ?? []).length > 0 && <Alert severity="warning" sx={{ mb: 1 }}>Import warnings: {p.warnings.join(" · ")}</Alert>}
                <Typography variant="caption" color="text.secondary">{importedAt}</Typography>
                {p.sdlbs.map(s => (
                    <Paper key={s.sdlbId} variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
                        <Typography variant="subtitle1">
                            {s.sdlbId}{" "}
                            <Typography component="span" variant="body2" color="text.secondary">
                                {s.running ? `running: ${s.running.eventId} v${s.running.eventVersion}, step ${s.running.currentStep + 1} / ${s.running.totalSteps}, ends ${utcFull(s.running.endDate)}` : "no event running"}
                                {!s.inImport ? " · NOT IN THE IMPORT" : ""}
                            </Typography>
                        </Typography>
                        {(s.blockers ?? []).map((b, i) => <Alert key={i} severity="error" sx={{ mt: 1 }}>{b}</Alert>)}
                        {(s.warnings ?? []).map((w, i) => <Alert key={i} severity="warning" sx={{ mt: 1 }}>{w}</Alert>)}
                        {s.running && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Applies now to the running event</Typography>
                                {s.live.length === 0 && <Typography variant="body2" color="text.secondary">nothing changes on the running event</Typography>}
                                <List dense disablePadding>
                                    {s.live.map((c, i) => line(
                                        c.from == null && c.to == null ? `${c.field}` : `${c.field}: ${c.from ?? "—"} → ${c.to ?? "—"}`,
                                        c.note))}
                                </List>
                            </Box>
                        )}
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>From the next event on</Typography>
                            {s.nextEvent.length === 0 && <Typography variant="body2" color="text.secondary">no change</Typography>}
                            <List dense disablePadding>
                                {s.nextEvent.map((c, i) => line(`${c.eventId} · ${c.field}: ${c.from ?? "—"} → ${c.to ?? "—"}`))}
                            </List>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Schedule</Typography>
                            {s.schedule.length === 0 && <Typography variant="body2" color="text.secondary">no change</Typography>}
                            <List dense disablePadding>
                                {s.schedule.map((c, i) => line(`${c.change}: ${c.eventId} ${utc(c.startDate)} → ${utc(c.endDate)}${c.fromEndDate ? ` (was → ${utc(c.fromEndDate)})` : ""}`))}
                            </List>
                            <Typography variant="body2" color="text.secondary">
                                next event: {nextEventText(s.next?.from)}{JSON.stringify(s.next?.from) !== JSON.stringify(s.next?.to) ? ` → ${nextEventText(s.next?.to)}` : ""}
                            </Typography>
                        </Box>
                    </Paper>
                ))}
            </Box>
        );
    }

    render() {
        const { ids, sdlbId, branch, eventVersion, events, data, loadedAt, loading } = this.state;
        const eventOptions = [{ value: LATEST, label: "Latest" }, ...events.map(ev => ({ value: ev.eventVersion, label: `v${ev.eventVersion} ${ev.eventId} · ${utc(ev.startDate)} → ${utc(ev.endDate)}${ev.isDone ? "" : " (running)"}` }))];
        return (
            <PageContainer>
                <SectionHeader title="SDLB">
                    {loadedAt && <Typography variant="caption" color="text.secondary">updated {relTime(loadedAt)}</Typography>}
                    <IconButton size="small" onClick={() => this.load()} disabled={loading}><RefreshIcon /></IconButton>
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="SDLB" size="small" minWidth={160} value={sdlbId} options={ids ?? []} onChange={v => this.select({ sdlbId: v })} />
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={BRANCHES} onChange={v => this.select({ branch: v })} />
                    <SelectField label="Event" size="small" minWidth={300} value={eventVersion ?? LATEST} options={eventOptions} onChange={v => this.select({ eventVersion: v === LATEST ? null : v })} />
                </SelectorBar>
                {ids && ids.length === 0 && <Alert severity="info">This title has no SDLB.</Alert>}
                {data && this.renderInfo()}
                {data && canEdit() && this.renderActions()}
                {data && !data.event && data.schedule && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Schedule on meta patch {data.metaPatch}: {data.schedule.map(s => `${s.eventId} ${utc(s.startDate)} → ${utc(s.endDate)}`).join(" · ") || "no rows"}
                    </Alert>
                )}
                {data?.event && this.renderDays()}
                {this.renderInspect()}
                {this.renderReimport()}
            </PageContainer>
        );
    }
}
